import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Modal,
  ModalDialog,
  ModalClose,
  Button,
  ButtonGroup,
  Chip,
  Divider,
  Alert,
} from '@mui/joy';
import { Edit, Delete, Visibility, Publish, Add, Download } from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';
import { scheduleService } from '@/services/scheduleService';
import { tagService } from '@/services/tagService';
import { projectService } from '@/services/projectService';
import { ScheduleWithDetails } from '@/types/schedule';
import { TaskWithAssignees, TASK_STATUS_LABELS, TASK_PRIORITY_LABELS } from '@/types/project';
import Calendar from '@/components/Calendar';
import TaskStatusChip from '@/components/TaskStatusChip';
import TaskPriorityChip from '@/components/TaskPriorityChip';
import BulkScheduleModal from '@/components/BulkScheduleModal';
import ScheduleFormModal from '@/components/ScheduleFormModal';
import { ScheduleTagManager } from '@/components/ScheduleTagManager';
import toast from 'react-hot-toast';



export default function CalendarPage() {
  const { permissions, user } = useAuth();
  const [schedules, setSchedules] = useState<ScheduleWithDetails[]>([]);
  const [scheduleTags, setScheduleTags] = useState<Record<number, any[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleWithDetails | null>(null);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showTasks, setShowTasks] = useState(true);
  const [calendarTasks, setCalendarTasks] = useState<TaskWithAssignees[]>([]);
  const [selectedTask, setSelectedTask] = useState<TaskWithAssignees | null>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);


  // Helper function to check permissions
  const hasPermission = (feature: string, action: string) => {
    const permissionName = `${feature}.${action}`;
    return permissions.includes(permissionName);
  };

  // Fetch schedules and tags
  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        setIsLoading(true);
        const schedulesData = await scheduleService.getAllSchedules();
        setSchedules(schedulesData);

        // Fetch tags for all schedules in bulk
        if (schedulesData.length > 0) {
          try {
            const scheduleIds = schedulesData.map(schedule => schedule.id);
            const tagsData = await tagService.getBulkScheduleTags(scheduleIds);
            setScheduleTags(tagsData);
          } catch (error) {
            console.error('CalendarPage: Failed to fetch schedule tags:', error);
            // Don't show error toast for tags, as it's not critical
          }
        }
      } catch (error) {
        console.error('Failed to fetch schedules:', error);
        toast.error('Failed to load schedules');
      } finally {
        setIsLoading(false);
      }
    };

    if (permissions.length > 0 || user) {
      fetchSchedules();
    }
  }, [permissions, user]);

  // Fetch tasks for the calendar (privacy-aware: backend filters based on user permissions)
  useEffect(() => {
    const fetchTasks = async () => {
      if (!user) return;
      if (!hasPermission('projects', 'view')) return;
      try {
        const tasks = await projectService.getTasks();
        setCalendarTasks(tasks.filter(t => t.status !== 'completed'));
      } catch {
        // non-critical; tasks are supplementary to schedules
      }
    };
    if (user) {
      fetchTasks();
    }
  }, [user, permissions]);

  const handleScheduleClick = (schedule: ScheduleWithDetails) => {
    setSelectedSchedule(schedule);
    setIsModalOpen(true);
  };

  const handleAddSchedule = () => {
    setIsCreateModalOpen(true);
  };

  const handleCreateSuccess = async () => {
    // Refresh schedules after successful creation
    try {
      const updatedSchedules = await scheduleService.getAllSchedules();
      setSchedules(updatedSchedules);
      
      // Refresh tags if there are schedules
      if (updatedSchedules.length > 0) {
        try {
          const scheduleIds = updatedSchedules.map(s => s.id);
          const tagsData = await tagService.getBulkScheduleTags(scheduleIds);
          setScheduleTags(tagsData);
        } catch (error) {
          console.error('Failed to refresh tags after creation:', error);
        }
      }
    } catch (error) {
      console.error('Failed to refresh schedules after creation:', error);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSchedule(null);
  };

  const handleOpenBulkModal = () => {
    setIsBulkModalOpen(true);
  };

  const handleCloseBulkModal = () => {
    setIsBulkModalOpen(false);
  };

  const handleBulkSuccess = () => {
    // Refresh schedules after successful bulk creation
    const fetchData = async () => {
      try {
        const updatedSchedules = await scheduleService.getAllSchedules();
        setSchedules(updatedSchedules);
        
        // Refresh tags if there are schedules
        if (updatedSchedules.length > 0) {
          try {
            const scheduleIds = updatedSchedules.map(s => s.id);
            const tagsData = await tagService.getBulkScheduleTags(scheduleIds);
            setScheduleTags(tagsData);
          } catch (error) {
            console.error('Failed to refresh tags after bulk creation:', error);
          }
        }
      } catch (error) {
        console.error('Failed to refresh schedules after bulk creation:', error);
      }
    };
    fetchData();
  };

  const handleExportICS = async (scheduleId: number) => {
    try {
      const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';
      const token = localStorage.getItem('accessToken');

      const response = await fetch(`${API_BASE_URL}/schedules/${scheduleId}/export/ics`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to export schedule');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers.get('content-disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') || `schedule-${scheduleId}.ics`
        : `schedule-${scheduleId}.ics`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Schedule exported successfully!');
    } catch (error: any) {
      console.error('Export failed:', error);
      toast.error(error.message || 'Failed to export schedule');
    }
  };

  const handleDelete = async (scheduleId: number) => {
    if (!confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    try {
      await scheduleService.deleteSchedule(scheduleId);
      setSchedules(schedules.filter(s => s.id !== scheduleId));
      toast.success('Schedule deleted successfully');
      handleCloseModal();
    } catch (error) {
      console.error('Failed to delete schedule:', error);
      toast.error('Failed to delete schedule');
    }
  };

  const handlePublish = async (scheduleId: number) => {
    try {
      await scheduleService.publishSchedules({ scheduleIds: [scheduleId] });
      setSchedules(schedules.map(s => 
        s.id === scheduleId ? { ...s, status: 'published' } : s
      ));
      toast.success('Schedule published successfully');
    } catch (error) {
      console.error('Failed to publish schedule:', error);
      toast.error('Failed to publish schedule');
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    return status === 'published' ? 'success' : 'warning';
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading calendar...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography level="h1" mb={1}>Calendar</Typography>
          <Typography level="body-sm" color="neutral">
            View and manage schedules in calendar format
          </Typography>
        </Box>
        {hasPermission('schedules', 'create') && (
          <Button
            startDecorator={<Add />}
            onClick={handleOpenBulkModal}
            size="sm"
          >
            Bulk Schedule
          </Button>
        )}
      </Stack>      

      <Calendar
        schedules={schedules}
        onScheduleClick={handleScheduleClick}
        onAddSchedule={hasPermission('schedules', 'create') ? handleAddSchedule : undefined}
        scheduleTags={scheduleTags}
        onTagsChange={async () => {
          try {
            const scheduleIds = schedules.map(s => s.id);
            if (scheduleIds.length > 0) {
              const tagsData = await tagService.getBulkScheduleTags(scheduleIds);
              setScheduleTags(tagsData);
            }
          } catch (error) {
            console.error('Failed to refresh tags after change:', error);
          }
        }}
        tasks={calendarTasks}
        showTasks={showTasks}
        onTaskClick={(task) => { setSelectedTask(task); setIsTaskModalOpen(true); }}
      />

      {/* Schedule Details Modal */}
      <Modal open={isModalOpen && selectedSchedule !== null} onClose={handleCloseModal}>
        <ModalDialog size="lg">
          <ModalClose />
          <Typography level="h4" mb={2}>
            Schedule Details
          </Typography>

          {selectedSchedule && (
            <Stack spacing={3}>
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Box>
                      <Typography level="body-sm" color="neutral">
                        Employee
                      </Typography>
                      <Typography level="body-lg">
                        {selectedSchedule.employee?.first_name} {selectedSchedule.employee?.last_name}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography level="body-sm" color="neutral">
                        Shift Type
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: selectedSchedule.shift_type?.color,
                          }}
                        />
                        <Typography level="body-lg">
                          {selectedSchedule.shift_type?.name}
                        </Typography>
                      </Stack>
                    </Box>

                    <Box>
                      <Typography level="body-sm" color="neutral">
                        Start Time
                      </Typography>
                      <Typography level="body-lg">
                        {formatDateTime(selectedSchedule.start_datetime)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography level="body-sm" color="neutral">
                        End Time
                      </Typography>
                      <Typography level="body-lg">
                        {formatDateTime(selectedSchedule.end_datetime)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography level="body-sm" color="neutral">
                        Status
                      </Typography>
                      <Chip
                        size="sm"
                        color={getStatusColor(selectedSchedule.status)}
                        variant="soft"
                      >
                        {selectedSchedule.status}
                      </Chip>
                    </Box>

                    <Box>
                      <Typography level="body-sm" color="neutral">
                        Created by
                      </Typography>
                      <Typography level="body-lg">
                        {selectedSchedule.created_by_user?.first_name} {selectedSchedule.created_by_user?.last_name}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              {/* Tags Section */}
              <Box>
                <ScheduleTagManager
                  scheduleId={selectedSchedule.id}
                  preFetchedTags={scheduleTags[selectedSchedule.id] || []}
                  onTagsChange={() => {
                    // Refresh schedules and tags to get updated information
                    const refreshData = async () => {
                      try {
                        const updatedSchedules = await scheduleService.getAllSchedules();
                        setSchedules(updatedSchedules);
                        
                        // Refresh tags in bulk
                        if (updatedSchedules.length > 0) {
                          const scheduleIds = updatedSchedules.map(s => s.id);
                          const tagsData = await tagService.getBulkScheduleTags(scheduleIds);
                          setScheduleTags(tagsData);
                        }
                      } catch (error) {
                        console.error('Failed to refresh data after tag change:', error);
                      }
                    };
                    refreshData();
                  }}
                />
              </Box>

              <Divider />

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                {hasPermission('schedules', 'view') && (
                  <Button
                    variant="outlined"
                    startDecorator={<Download />}
                    onClick={() => handleExportICS(selectedSchedule.id)}
                  >
                    Export as ICS
                  </Button>
                )}
                {hasPermission('schedules', 'edit') && (
                  <Button
                    variant="outlined"
                    startDecorator={<Edit />}
                    onClick={() => {/* Handle edit action */}}
                  >
                    Edit
                  </Button>
                )}
                {hasPermission('schedules', 'delete') && (
                  <Button
                    variant="outlined"
                    color="danger"
                    startDecorator={<Delete />}
                    onClick={() => handleDelete(selectedSchedule.id)}
                  >
                    Delete
                  </Button>
                )}
                {selectedSchedule.status === 'draft' && hasPermission('schedules', 'publish') && (
                  <Button
                    startDecorator={<Publish />}
                    onClick={() => handlePublish(selectedSchedule.id)}
                  >
                    Publish
                  </Button>
                )}
                <Button variant="outlined" onClick={handleCloseModal}>
                  Close
                </Button>
              </Stack>
            </Stack>
          )}
        </ModalDialog>
      </Modal>

      {/* Create Schedule Modal */}
      <ScheduleFormModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Bulk Schedule Modal */}
      <BulkScheduleModal
        open={isBulkModalOpen}
        onClose={handleCloseBulkModal}
        onSuccess={handleBulkSuccess}
      />

      {/* Task Detail Modal */}
      <Modal open={isTaskModalOpen && selectedTask !== null} onClose={() => { setIsTaskModalOpen(false); setSelectedTask(null); }}>
        <ModalDialog size="lg">
          <ModalClose />
          <Typography level="h4" mb={2}>Task Details</Typography>
          {selectedTask && (
            <Stack spacing={2}>
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={1.5}>
                    <Typography level="title-lg">{selectedTask.name}</Typography>
                    {selectedTask.description && <Typography level="body-md">{selectedTask.description}</Typography>}
                    {selectedTask.project_name && (
                      <Typography level="body-sm" color="neutral">
                        Project: {selectedTask.project_name}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={2} alignItems="center">
                      <TaskStatusChip status={selectedTask.status} />
                      <TaskPriorityChip priority={selectedTask.priority} />
                    </Stack>
                    {selectedTask.due_date && (
                      <Typography level="body-sm">Due: {new Date(selectedTask.due_date).toLocaleDateString()}</Typography>
                    )}
                    {selectedTask.shift_type && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: selectedTask.shift_type.color }} />
                        <Typography level="body-sm">{selectedTask.shift_type.name}</Typography>
                      </Stack>
                    )}
                    {selectedTask.assignees.length > 0 && (
                      <Box>
                        <Typography level="body-sm" color="neutral" sx={{ mb: 0.5 }}>Assignees:</Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {selectedTask.assignees.map(a => (
                            <Chip key={a.id} size="sm" variant="outlined">{a.first_name} {a.last_name}</Chip>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
              <Stack direction="row" justifyContent="flex-end">
                <Button variant="outlined" onClick={() => { setIsTaskModalOpen(false); setSelectedTask(null); }}>Close</Button>
              </Stack>
            </Stack>
          )}
        </ModalDialog>
      </Modal>
    </Box>
  );
} 